import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Toaster } from 'sonner';
import Login from '@/pages/Login';
import Apply from '@/pages/Apply';
import BillingSetup from '@/pages/BillingSetup';
import OpsLayout from '@/components/Layout/OpsLayout';
import PortalLayout from '@/components/Layout/PortalLayout';
import VendorLayout from '@/pages/vendor/VendorLayout';
import OpsDashboard from '@/pages/ops/OpsDashboard';
import Customers from '@/pages/ops/Customers';
import CustomerDetail from '@/pages/ops/CustomerDetail';
import RepairEvents from '@/pages/ops/RepairEvents';
import RepairDetail from '@/pages/ops/RepairDetail';
import Invoices from '@/pages/ops/Invoices';
import Users from '@/pages/ops/Users';
import PmDot from '@/pages/ops/PmDot';
import Chat from '@/pages/ops/Chat';
import Vendors from '@/pages/ops/Vendors';
import Applications from '@/pages/ops/Applications';
import PortalDashboard from '@/pages/portal/PortalDashboard';
import PortalRepairs from '@/pages/portal/PortalRepairs';
import PortalInvoices from '@/pages/portal/PortalInvoices';
import PortalUnits from '@/pages/portal/PortalUnits';
import RequestService from '@/pages/portal/RequestService';
import CustomerAnalytics from '@/pages/portal/CustomerAnalytics';
import VendorOrders from '@/pages/vendor/VendorOrders';
import SecurityCenter from '@/pages/ops/SecurityCenter';
import VendorPricing from '@/pages/ops/VendorPricing';
import SheetsSync from '@/pages/ops/SheetsSync';
import ImportCenter from '@/pages/ops/ImportCenter';
import InvoiceTransformer from '@/pages/ops/InvoiceTransformer';
import SupplierDetail from '@/pages/ops/SupplierDetail';
import InvoiceBuilder from '@/pages/ops/InvoiceBuilder';
import FinalizeInvoice from '@/pages/ops/FinalizeInvoice';
import CreateInvoice from '@/pages/ops/CreateInvoice';
import Statistics from '@/pages/ops/Statistics';
import AuditHistory from '@/pages/ops/AuditHistory';
import AccountHealth from '@/pages/ops/AccountHealth';
import Benchmarking from '@/pages/ops/Benchmarking';
import ROICalculator from '@/pages/ops/ROICalculator';
import IntakeInbox from '@/pages/ops/IntakeInbox';
import SalesDashboard from '@/pages/ops/SalesDashboard';
import Pipeline from '@/pages/ops/Pipeline';
import DataReset from '@/pages/admin/DataReset';
import NotificationsInbox from '@/pages/ops/NotificationsInbox';
import Profile from '@/pages/Profile';
import Settings from '@/pages/ops/Settings';
import RepairCategoriesAdmin from '@/pages/ops/RepairCategoriesAdmin';
import VendorMap from '@/pages/ops/VendorMap';

import InstallPrompt from '@/components/pwa/InstallPrompt';

const OPS_ROLES = ['master_admin', 'admin', 'sales_admin', 'sales', 'accounting', 'moderator', 'operator'];

function Guard({ children, roles }) {
  const { token, user } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user?.role)) {
    if (user?.role === 'customer') return <Navigate to="/portal/dashboard" replace />;
    if (user?.role === 'vendor') return <Navigate to="/vendor/orders" replace />;
    return <Navigate to="/ops/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  const { token, user } = useAuth();
  const home = !token ? '/login'
    : user?.role === 'customer' ? '/portal/dashboard'
    : user?.role === 'vendor' ? '/vendor/orders'
    : '/ops/dashboard';

  return (
    <Routes>
      <Route path="/login" element={token ? <Navigate to={home} replace /> : <Login />} />
      <Route path="/apply" element={<Apply />} />
      <Route path="/billing-setup/:token" element={<BillingSetup />} />

      {/* Ops Console */}
      <Route path="/ops" element={<Guard roles={OPS_ROLES}><OpsLayout /></Guard>}>
        <Route index element={<Navigate to="/ops/dashboard" replace />} />
        <Route path="dashboard" element={<OpsDashboard />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/:id" element={<CustomerDetail />} />
        <Route path="repairs" element={<RepairEvents />} />
        <Route path="repairs/:id" element={<RepairDetail />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="pm-dot" element={<PmDot />} />
        <Route path="chat" element={<Chat />} />
        <Route path="chat/:threadId" element={<Chat />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="vendors/:vendorId/pricing" element={<VendorPricing />} />
        <Route path="sheets-sync" element={<SheetsSync />} />
        <Route path="import-center" element={<ImportCenter />} />
        <Route path="applications" element={<Applications />} />
        <Route path="intake-inbox" element={<IntakeInbox />} />
        <Route path="sales" element={<SalesDashboard />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="users" element={<Guard roles={['master_admin','admin']}><Users /></Guard>} />
        <Route path="security" element={<Guard roles={['master_admin','admin']}><SecurityCenter /></Guard>} />
        <Route path="invoice-transformer" element={<InvoiceTransformer />} />
        <Route path="invoice-transformer/supplier/:id" element={<SupplierDetail />} />
        <Route path="invoice-transformer/build/:supplierId" element={<InvoiceBuilder />} />
        <Route path="invoice-transformer/finalize/:runId" element={<FinalizeInvoice />} />
        <Route path="create-invoice" element={<CreateInvoice />} />
        <Route path="statistics" element={<Statistics />} />
        <Route path="audit-history" element={<Guard roles={['master_admin','admin']}><AuditHistory /></Guard>} />
        <Route path="account-health" element={<AccountHealth />} />
        <Route path="analytics/benchmarking" element={<Benchmarking />} />
        <Route path="analytics/roi/:customerId" element={<ROICalculator />} />
        <Route path="profile" element={<Profile />} />
        <Route path="settings" element={<Guard roles={['master_admin','admin']}><Settings /></Guard>} />
        <Route path="settings/repair-categories" element={<Guard roles={['master_admin','admin']}><RepairCategoriesAdmin /></Guard>} />
        <Route path="data-reset" element={<Guard roles={['master_admin']}><DataReset /></Guard>} />
        <Route path="vendor-map" element={<VendorMap />} />
        <Route path="notifications" element={<NotificationsInbox />} />
      </Route>
      <Route path="/portal" element={<Guard roles={['customer']}><PortalLayout /></Guard>}>
        <Route index element={<Navigate to="/portal/dashboard" replace />} />
        <Route path="dashboard" element={<PortalDashboard />} />
        <Route path="repairs" element={<PortalRepairs />} />
        <Route path="invoices" element={<PortalInvoices />} />
        <Route path="units" element={<PortalUnits />} />
        <Route path="request-service" element={<RequestService />} />
        <Route path="analytics" element={<CustomerAnalytics />} />
        <Route path="profile" element={<Profile />} />
      </Route>

      {/* Vendor Portal */}
      <Route path="/vendor" element={<Guard roles={['vendor']}><VendorLayout /></Guard>}>
        <Route index element={<Navigate to="/vendor/orders" replace />} />
        <Route path="orders" element={<VendorOrders />} />
      </Route>

      <Route path="/" element={<Navigate to={home} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster richColors theme="dark" position="top-right" />
        <InstallPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
